import { MapView } from './components/MapView';
import { Popup } from './components/Popup';
import { FocusTrap } from './modules/focusTrap';
import {
  IMG_BASE,
  SESSION_KEY,
  MIN_LOADING_MS_FIRST_VISIT,
  HOWTO_SHOW_DELAY_MS_PC,
  HOWTO_SHOW_DELAY_MS_SP,
  HOWTO_DISPLAY_MS,
  PC_BREAKPOINT_PX,
} from './data/constants';
import './common/list/article-list';
import {
  loadRuntimeApiConfig,
  logStagingZetaApiUsage,
} from './common/config/runtime-api-config';

const appElement = document.getElementById('app');
const popupLayer = document.getElementById('popup-layer');

// ── popup 要素をクリック可能にする（popup-layer は透視） ──
const styleOverride = document.createElement('style');
styleOverride.textContent = `
  /* 表示中のポップアップのみクリック可能 */
  #popup-layer .popup:not(.popup--hidden) {
    pointer-events: auto !important;
  }
  /* 非表示のポップアップはクリック不可 */
  #popup-layer .popup.popup--hidden {
    pointer-events: none !important;
  }
  /* ピンは常にクリック可能 */
  #popup-layer .popup__pin {
    pointer-events: auto !important;
  }
`;
document.head.appendChild(styleOverride);

// ── マップコンテナ（#popup-layer の直前 = DOM 最背面）────
const mapContainer = document.createElement('div');
mapContainer.id = 'map-container';
mapContainer.style.pointerEvents = 'none';
appElement.insertBefore(mapContainer, popupLayer);

// ── 操作説明オーバーレイ ───────────────────────────────────
const loadingOverlay = document.getElementById('loading-overlay');
const howToOverlay = document.createElement('div');
howToOverlay.id = 'howto';

function isTouchDevice() {
  return !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

if (isTouchDevice()) {
  howToOverlay.innerHTML = `
    <div class="howto__catch">マップを自由に散策して、<br>気になるエリアを覗いてみよう！</div>
    <div class="howto__items">
      <div class="howto__item">
        <img src="${IMG_BASE}/icon-drag-hand.svg" class="howto__icon" alt="ドラッグ" width="96" height="70" />
        <span>マップをドラッグ</span>
      </div>
      <div class="howto__item">
        <img src="${IMG_BASE}/icon-tap-hand.svg" class="howto__icon" alt="タップ" width="84" height="70" />
        <span>エリアをタップ</span>
      </div>
    </div>
  `;
} else {
  howToOverlay.innerHTML = `
    <div class="howto__catch">マップを自由に散策して、<br>気になるエリアを覗いてみよう！</div>
      <div class="howto__items">
      <div class="howto__item">
        <img src="${IMG_BASE}/icon-drag-mouse.svg" class="howto__icon" alt="ドラッグ" width="96" height="60" />
        <span>マップをつかんで<br>ドラッグ</span>
      </div>
      <div class="howto__item">
        <img src="${IMG_BASE}/icon-scroll-mouse.svg" class="howto__icon" alt="スクロール" width="60" height="60" />
        <span>ホイールで<br>拡大・縮小</span>
      </div>
      <div class="howto__item">
        <img src="${IMG_BASE}/icon-click-mouse.svg" class="howto__icon" alt="クリック" width="84" height="60" />
        <span>エリアをクリック</span>
      </div>
    </div>
  `;
}

appElement.insertBefore(howToOverlay, loadingOverlay);

// ── コンポーネント初期化 ───────────────────────────────────
const popup = new Popup(popupLayer, (district, immediate) => {
  showDistrict(district, immediate);
}, (district) => {
  openModal(district);
});

// index2.html ではピン表示→吹き出し（showExpanded=false）
const isIndex2 = window.location.pathname.includes('index2');

const mapView = new MapView(mapContainer, (district) => {
  dismissHowToOverlay();
  showDistrict(district, true);
  requestAnimationFrame(() => {
    openModal(district);
  });
}, () => {
  if (popup.isVisible()) popup.hide();
});

startLoadingTracking();

// ── 操作説明オーバーレイ ────────────────────────────────
howToOverlay.style.opacity = '0';
let isHowToDismissed = false;
let howToDismissTimer = null;
let howToShowTimer = null;
const HOWTO_SHOW_DELAY_MS = window.innerWidth >= PC_BREAKPOINT_PX ? HOWTO_SHOW_DELAY_MS_PC : HOWTO_SHOW_DELAY_MS_SP;

function startHowToTimer() {
  howToShowTimer = setTimeout(() => {
    if (isHowToDismissed) return;
    howToOverlay.style.removeProperty('opacity');
    howToDismissTimer = setTimeout(() => dismissHowToOverlay(), HOWTO_DISPLAY_MS);
  }, HOWTO_SHOW_DELAY_MS);
}

function dismissHowToOverlay() {
  if (isHowToDismissed) return;
  isHowToDismissed = true;
  if (howToShowTimer) clearTimeout(howToShowTimer);
  if (howToDismissTimer) clearTimeout(howToDismissTimer);
  howToOverlay.style.removeProperty('opacity');
  howToOverlay.classList.add('howto--gone');
  // インストラクション終了でマップの操作を有効化
  mapContainer.style.removeProperty('pointer-events');
  // index.html: インストラクション終了後に全ピンドロップ
  if (!isIndex2) {
    showAllPinsOnMap();
  }
}

function showAllPinsOnMap() {
  popup.showAllPins((d) => mapView.getDistrictScreenCenter(d));
}

// ── ローディング完了処理 ───────────────────────────────────
const MIN_LOADING_MS = sessionStorage.getItem(SESSION_KEY) ? 0 : MIN_LOADING_MS_FIRST_VISIT;
sessionStorage.setItem(SESSION_KEY, '1');

function onLoadingComplete() {
  popupLayer.style.removeProperty('visibility');
  const overlay = document.getElementById('loading-overlay');
  if (overlay) {
    overlay.classList.add('loading-overlay--done');
    setTimeout(() => {
      // 出現アニメーション開始
      document.body.classList.remove('is-loading');

      if (window.innerWidth < PC_BREAKPOINT_PX) {
        // SP: 出現アニメ完了後にクローズダウン + インストラクション
        setTimeout(() => {
          startHowToTimer();
          mapView.animateCloseDown();
        }, 1500);
      } else {
        startHowToTimer();
      }
    }, 1000);
    setTimeout(() => overlay.remove(), 2400);
  } else {
    document.body.classList.remove('is-loading');
    startHowToTimer();
  }
}

function startLoadingTracking() {
  const allImages = Array.from(document.querySelectorAll('#map-inner img'));
  const total = allImages.length;
  const progressFill = document.getElementById('loading-progress-fill');
  const startTime = Date.now();
  let loaded = 0;
  const counted = new WeakSet();

  function onOneLoaded(img) {
    if (counted.has(img)) return;
    counted.add(img);
    loaded++;
    if (progressFill) {
      progressFill.style.width = `${(loaded / total) * 100}%`;
    }
    if (loaded >= total) {
      const elapsed = Date.now() - startTime;
      const remain = Math.max(0, MIN_LOADING_MS - elapsed);
      setTimeout(onLoadingComplete, remain);
    }
  }

  if (total === 0) {
    setTimeout(onLoadingComplete, MIN_LOADING_MS);
    return;
  }
  for (const img of allImages) {
    if (img.complete) {
      // キャッシュ済み画像（正常・破損とも）を即カウント
      setTimeout(() => onOneLoaded(img), 0);
    } else {
      const handler = () => onOneLoaded(img);
      img.addEventListener('load', handler, { once: true });
      img.addEventListener('error', handler, { once: true });
    }
  }
}

// ── インタラクション ────────────────────────────────────────
let panGeneration = 0;

function showDistrict(district, showExpanded = false) {
  dismissHowToOverlay();
  if (popup.isVisible() && popup.getActiveDistrict()?.id === district.id) return;
  if (popup.isVisible()) popup.hide();
  const gen = ++panGeneration;
  mapView.panToDistrict(district, () => {
    // 高速切り替え時: 古いパンのコールバックが遅延発火した場合は無視する
    if (gen !== panGeneration) return;
    const screenCenter = mapView.getDistrictScreenCenter(district);
    popup.show(district, screenCenter, showExpanded);
    // 吹き出しが画面外にはみ出していたら追加パンで調整
    adjustPopupVisibility(district);
  });
}

function adjustPopupVisibility(district) {
  const screenCenter = mapView.getDistrictScreenCenter(district);
  const cardHeight = Popup.CARD_HEIGHT;
  const cardWidth = Popup.POPUP_WIDTH;
  const margin = 10;
  let shiftX = 0;
  let shiftY = 0;

  // 上方向はみ出し: ピン位置 - ピン高さ - リフト - カード高さ - カレット - マージン
  const popupTop = screenCenter.y - Popup.PIN_HEIGHT - 6 - cardHeight - 13 - margin;
  if (popupTop < 0) {
    shiftY = -popupTop + margin;
  }

  // 左方向はみ出し: カード中心 - カード幅半分
  const popupLeft = screenCenter.x - cardWidth / 2;
  if (popupLeft < margin) {
    shiftX = margin - popupLeft;
  }

  // 右方向はみ出し: カード中心 + カード幅半分
  const viewportWidth = window.innerWidth;
  const popupRight = screenCenter.x + cardWidth / 2;
  if (popupRight > viewportWidth - margin) {
    shiftX = viewportWidth - margin - popupRight;
  }

  if (shiftX !== 0 || shiftY !== 0) {
    mapView.animateOffsetShift(shiftX, shiftY);
  }
}

// ── ポップアップ追従 ─────────────────────────────────────
mapView.setOnTransformChange(() => {
  if (popup.allPinsVisible) {
    popup.positionAllPins((d) => mapView.getDistrictScreenCenter(d));
  }
  const district = popup.getActiveDistrict();
  if (district) {
    popup.position(mapView.getDistrictScreenCenter(district));
  }
});

mapView.setOnPanStart(() => {
  if (popup.isVisible()) popup.hide();
});

// ── キーボードナビゲーション: Tab でピンをフォーカスしたら自動パン ───
popup.setOnPinFocus((district) => {
  mapView.panToDistrictKbd(district);
});

// ── ポップアップ外クリックで閉じる ───────────────────────
document.addEventListener('pointerdown', (event) => {
  if (!popup.isVisible()) return;
  const activeEl = popup.getElement();
  if (activeEl && !activeEl.contains(event.target)) {
    popup.hide();
  }
}, { capture: true });

// ── モーダル ──────────────────────────────────────────────
const modals = new Map();
/** @type {Map<string, FocusTrap>} モーダルごとの FocusTrap インスタンス */
const modalTraps = new Map();
let activeModal = null;

document.querySelectorAll('.modal[data-district-id]').forEach((el) => {
  const districtId = el.dataset.districtId;
  modals.set(districtId, el);
  modalTraps.set(districtId, new FocusTrap(el));
  // a11y: 初期状態は非表示なので inert でキーボード/支援技術から隔離
  el.setAttribute('inert', '');
  // 各モーダルの閉じるボタン・オーバーレイにイベント設定
  el.querySelector('.modal__close')?.addEventListener('click', closeModal);
  el.querySelector('.modal__overlay')?.addEventListener('click', closeModal);
});

function openModal(district) {
  const el = modals.get(district.id);
  if (!el) return;
  // 他のモーダルが開いていたら閉じる
  if (activeModal && activeModal !== el) {
    const prevId = activeModal.dataset.districtId;
    modalTraps.get(prevId)?.deactivate();
    activeModal.classList.add('modal--hidden');
    activeModal.setAttribute('aria-hidden', 'true');
    activeModal.setAttribute('inert', '');
  }
  activeModal = el;
  el.classList.remove('modal--hidden');
  el.setAttribute('aria-hidden', 'false');
  el.removeAttribute('inert');
  // モーダルの aria-label を区名で設定（スクリーンリーダー対応）
  const heading = el.querySelector('.modal__heading');
  if (heading) {
    el.setAttribute('aria-label', heading.textContent.trim());
  }
  // モーダル表示中はポップアップのキーボードナビを抑制
  popup.suppressKeyboard = true;
  // フォーカストラップ起動
  modalTraps.get(district.id)?.activate();
}

function closeModal() {
  if (!activeModal) return;
  const districtId = activeModal.dataset.districtId;
  modalTraps.get(districtId)?.deactivate();
  activeModal.classList.add('modal--hidden');
  activeModal.setAttribute('aria-hidden', 'true');
  activeModal.setAttribute('inert', '');
  activeModal = null;
  popup.suppressKeyboard = false;
  // モーダルを開いた区のピンにフォーカスを戻す
  // FocusTrap の previousFocus（前の区のピンになりうる）より確実に正しい区へ戻るよう上書き
  const pin = document.querySelector(`#popup-layer .popup__pin[data-district-id="${districtId}"]`);
  if (pin) {
    pin.focus({ preventScroll: true });
  }
}

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && activeModal) {
    closeModal();
  }
});

// ── ピンテキスト新着更新 ─────────────────────────────────────
(async function updatePinTextsForNew() {
  // DOM が未確定の場合は DOMContentLoaded まで待つ
  if (document.readyState === 'loading') {
    await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
  }

  const runtimeConfig = await loadRuntimeApiConfig();
  const ENDPOINT = runtimeConfig.searchEndpoint;
  if (!ENDPOINT) return;

  const NEW_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  function parseArticleDate(raw) {
    if (!raw) return null;
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
    if (/^\d{4}\.\d{2}\.\d{2}$/.test(s)) {
      const [y, m, d] = s.split('.');
      return new Date(`${y}-${m}-${d}`);
    }
    if (/^\d{8}$/.test(s)) return new Date(`${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`);
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const tasks = [];
  document.querySelectorAll('.modal[data-district-id]').forEach((modal) => {
    const districtId = modal.dataset.districtId;
    const listEl = modal.querySelector('.js-article-list[data-category]');
    if (!listEl) return;
    const category = listEl.dataset.category;
    const pinTextEl = document.querySelector(`#popup-${districtId} .popup__pin--text`);
    if (!pinTextEl) return;

    tasks.push((async () => {
      try {
        const params = new URLSearchParams({ a: 'date', l: '1', p: '1', category });
        logStagingZetaApiUsage(ENDPOINT);
        const res = await fetch(`${ENDPOINT}?${params}`, { credentials: 'omit' });
        if (!res.ok) return;
        const json = await res.json();
        const result = json?.result ?? json ?? {};
        const items = Array.isArray(result.items) ? result.items
          : Array.isArray(result.list) ? result.list : [];
        if (!items.length) return;
        const dt = parseArticleDate(items[0].date);
        if (!dt) return;
        const diff = now - dt.getTime();
        if (diff >= 0 && diff <= NEW_DAYS_MS) {
          const m = dt.getMonth() + 1;
          const d = dt.getDate();
          pinTextEl.innerHTML = `${m}/${d}<br>NEW`;
        }
      } catch (_) {}
    })());
  });

  await Promise.all(tasks);
})();
